import React from "react";
import {
  FiLayers,
  FiTool,
  FiCode,
  FiGlobe,
  FiZap,
  FiMonitor,
} from "react-icons/fi";
import { MdOutlineApi, MdOutlineCategory } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const CustomAIAgent = () => {
  const features = [
    {
      title: "Tailored Autonomous Agents",
      description:
        "Design agents aligned to your workflows, policies, and KPIs with bespoke reasoning patterns.",
      icon: <FiLayers />,
    },
    {
      title: "Domain-Specific LLM Workflows",
      description:
        "Fine-tune and ground LLMs in your terminology, datasets, and compliance requirements for precise outputs.",
      icon: <MdOutlineCategory />,
    },
    {
      title: "Custom Pipelines",
      description:
        "Build ingestion, retrieval, and orchestration pipelines that keep agents current with enterprise knowledge.",
      icon: <FiCode />,
    },
    {
      title: "API & System Integrations",
      description:
        "Connect agents to CRMs, ERPs, knowledge bases, and productivity tools with secure adapters.",
      icon: <MdOutlineApi />,
    },
    {
      title: "Tool Augmentation",
      description:
        "Equip agents with calculators, RPA bots, or internal microservices for action-oriented automation.",
      icon: <FiTool />,
    },
    {
      title: "Observability & Control",
      description:
        "Central dashboards track prompts, outcomes, costs, and guardrails to ensure safe operation.",
      icon: <FiMonitor />,
    },
  ];

  const steps = [
    {
      title: "Use Case Prioritization",
      description:
        "Workshops identify high-impact workflows, success metrics, and governance expectations.",
    },
    {
      title: "Architecture & Prompt Design",
      description:
        "Define model stack, memory design, toolset, and prompt strategies tailored to your landscape.",
    },
    {
      title: "Build, Integrate, Validate",
      description:
        "Develop agents, connect systems, run eval harnesses, and harden for production use.",
    },
    {
      title: "Operate & Scale",
      description:
        "Monitor performance, retrain models, and expand agent coverage with continuous feedback.",
    },
  ];

  const benefits = [
    "Agents match your domain language, policies, and workflows",
    "Faster delivery via reusable accelerators and integration kits",
    "Secure, audited interactions with enterprise applications",
    "Scalable architecture ready for future agents and features",
    "Lower operational risk with testing and human oversight",
    "Measurable ROI through productivity, accuracy, and CX gains",
  ];

  const expertise = [
    "End-to-end team spanning strategy, ML engineering, and product design",
    "Experience with OpenAI, Anthropic, Azure OpenAI, Cohere, and open-source models",
    "Custom retrieval pipelines using Pinecone, Weaviate, Elasticsearch, and pgvector",
    "Security, risk, and compliance specialists ensuring data governance",
    "Designers crafting intuitive agent consoles and supervisory tools",
    "Managed services for monitoring, cost control, and continuous improvement",
  ];

   const image = [
    {
      side_image: "../assets/images/ai-automation/9.webp",
    }

  ];

  const useCases = [
    {
      industry: "Legal",
      title: "Case Preparation Agent",
      description:
        "Summarizes case law, drafts motions, and cites precedents with attorney-reviewed guardrails.",
    },
    {
      industry: "Manufacturing",
      title: "Engineering Knowledge Agent",
      description:
        "Ingests CAD notes, SOPs, and sensor data to guide plant engineers and technicians.",
    },
    {
      industry: "Media",
      title: "Content Production Assistant",
      description:
        "Coordinates research, asset creation, and metadata tagging across editorial teams.",
    },
    {
      industry: "Enterprise IT",
      title: "Knowledge Ops Agent",
      description:
        "Answers employee queries, drafts SOPs, and automates approvals via ITSM integrations.",
    },
  ];

  const reasons = [
    "Modular framework to add new agents or expand existing ones quickly",
    "Transparent governance with evaluation harnesses and audit logs",
    "Strong change management ensuring adoption and trust",
    "Co-innovation model aligning success metrics with client outcomes",
    "Flexible deployment options across cloud, on-prem, or VPC",
    "Dedicated support pods for tuning, retraining, and enablement",
  ];

  return (
    <AIAutomationTemplate
      title="Custom AI Agent Development"
      heroDescription="Build bespoke AI agents that understand your domain, connect to your systems, and deliver measurable business outcomes."
      about={{
        heading: "About Custom AI Agent Development",
        description:
          "From discovery to run operations, we partner with you to design agent ecosystems tailored to your industry, compliance, and technology stacks.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Plan Your Custom Agent"
      meta={{
        title: "Custom AI Agent Development | Bespoke Agentic Solutions | Ayanshtech",
        description:
          "Tailored autonomous agents with domain-specific LLM workflows, custom pipelines, and secure integrations.",
        ogTitle: "Custom AI Agent Services",
        ogDescription:
          "Design custom AI agents with domain-specific workflows, pipelines, and API integrations.",
        ogUrl: "http://ayanshtechnology.com/custom-ai-agent",
        canonical: "http://ayanshtechnology.com/custom-ai-agent",
      }}
    />
  );
};

export default CustomAIAgent;


